import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { fetchUserPreferences, saveUserPreference } from '../lib/userPreferences';
import { accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount } from '../lib/accountStorage';

type Theme = 'light' | 'dark';

export const METANOIA_PRIMARY = 'metanoia';
export const METANOIA_GRADIENT = 'linear-gradient(135deg, #3CC2DD, #A739E2, #F58C56)';
export const METANOIA_GRADIENT_BAR = 'linear-gradient(90deg, #3CC2DD, #A739E2, #F58C56)';
export const METANOIA_MIDDLE_COLOR = '#A739E2';
// The solid brand accent (calm cyan). Purple stays only inside the gradient
// string + SVG signature stops; the token accent is no longer purple.
export const METANOIA_ACCENT = '#3CC2DD';

interface ThemeState {
  theme: Theme;
  primaryColor: string;
  primaryColorCSS: string;
  isMetanoia: boolean;
  initializedUserId: string | null;
  cachedUserId: string | null;
  initialize: () => Promise<void>;
  setTheme: (theme: Theme) => void;
  setPrimaryColor: (color: string) => void;
}

function hexToRGB(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r} ${g} ${b}`;
}

function applyTheme(theme: Theme, primaryColor: string) {
  document.documentElement.classList.toggle('dark', theme === 'dark');

  const isMetanoia = primaryColor === METANOIA_PRIMARY;
  document.documentElement.classList.toggle('theme-metanoia', isMetanoia);

  if (isMetanoia) {
    document.documentElement.style.setProperty('--primary', hexToRGB(METANOIA_ACCENT));
    document.documentElement.style.setProperty('--primary-gradient', METANOIA_GRADIENT);
  } else {
    document.documentElement.style.setProperty('--primary', hexToRGB(primaryColor));
    document.documentElement.style.setProperty('--primary-gradient', 'none');
  }
}

export const useTheme = create<ThemeState>()(
  persist<ThemeState, [], [], any>(
    (set) => ({
      /* ⭐ DARK IS THE DEFAULT (owner, 2026-09-03). The surfaces that carry the
         brand — the onboarding sequence, the orb, the spectral gradient — are all
         drawn for a dark ground, and the app opened in light. A new account now
         starts in the look the product was designed in.
         ⚠️ This is only the DEFAULT. `initialize()` below still lets the stored
         `user_preferences.theme` win, so nobody who chose light is overridden. */
      theme: 'dark',
      // Brand (spectral gradient) is the default look; blue/etc. remain opt-in presets.
      primaryColor: METANOIA_PRIMARY,
      primaryColorCSS: METANOIA_ACCENT,
      isMetanoia: true,
      initializedUserId: null,
      cachedUserId: null,

      // Load-on-login from the shared user_preferences row (theme/primary_color)
      // and apply it. Keyed by user id; cloud-wins when present.
      initialize: async () => {
        const at = accountGeneration();
        const res = await fetchUserPreferences();
        // An answer for an account that has since signed out is not this copy's.
        if (at !== accountGeneration()) return;
        if (!res) return; // logged out
        if (useTheme.getState().initializedUserId === res.userId) return;
        const state = useTheme.getState();
        const cacheIsOurs =
          state.cachedUserId === null || state.cachedUserId === res.userId;
        const cloudTheme = res.row?.theme;
        const cloudColor = res.row?.primary_color;
        const theme: Theme =
          cloudTheme === 'light' || cloudTheme === 'dark'
            ? cloudTheme
            : cacheIsOurs
            ? state.theme
            // ⚠️ Another account's cache is not a signal — fall back to the
            // DEFAULT, not to light, or a shared device would still open light.
            : 'dark';
        const primaryColor =
          typeof cloudColor === 'string' && cloudColor
            ? cloudColor
            : cacheIsOurs
            ? state.primaryColor
            : METANOIA_PRIMARY;
        const isMetanoia = primaryColor === METANOIA_PRIMARY;
        set({
          theme,
          primaryColor,
          primaryColorCSS: isMetanoia ? METANOIA_ACCENT : primaryColor,
          isMetanoia,
          initializedUserId: res.userId,
          cachedUserId: res.userId,
        });
        applyTheme(theme, primaryColor);
      },

      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme, useTheme.getState().primaryColor);
        void saveUserPreference({ theme });
      },
      setPrimaryColor: (color) => {
        const isMetanoia = color === METANOIA_PRIMARY;
        set({
          primaryColor: color,
          primaryColorCSS: isMetanoia ? METANOIA_ACCENT : color,
          isMetanoia,
        });
        applyTheme(useTheme.getState().theme, color);
        void saveUserPreference({ primary_color: color });
      },
    }),
    {
      name: 'theme-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 3,
      migrate: (persistedState: any, version: number) => {
        try {
          const pc = persistedState?.primaryColor || METANOIA_PRIMARY;
          const isMeta = pc === METANOIA_PRIMARY;
          if (version < 3) {
            return {
              /* ⚠️ STAYS 'light' ON PURPOSE. This branch is an EXISTING user
                 being migrated, and the new dark default is for NEW accounts —
                 silently flipping someone's app to dark on an upgrade is a
                 change they did not ask for. */
              theme: persistedState?.theme || 'light',
              primaryColor: pc,
              primaryColorCSS: isMeta ? METANOIA_ACCENT : pc,
              isMetanoia: isMeta,
            } as unknown as ThemeState;
          }
          if (!persistedState || typeof persistedState !== 'object') {
            /* ⚠️⚠️ NOTHING STORED MEANS NOBODY HAS CHOSEN, so this must match the
               initial state above — it did not. It returned light AND a blue
               non-brand accent, so a fresh install that took this path opened
               looking like a different product entirely. */
            return {
              theme: 'dark',
              primaryColor: METANOIA_PRIMARY,
              primaryColorCSS: METANOIA_ACCENT,
              isMetanoia: true,
            } as unknown as ThemeState;
          }
          return {
            ...persistedState,
            primaryColorCSS: isMeta ? METANOIA_ACCENT : pc,
            isMetanoia: isMeta,
          } as ThemeState;
        } catch (error) {
          /* ⚠️ "Using defaults" has to mean the ACTUAL defaults. This returned
             light and a blue accent, so a failed migration quietly dropped the
             brand — the one moment a user is most likely to notice. */
          console.warn('Theme migration failed, using defaults:', error);
          return {
            theme: 'dark',
            primaryColor: METANOIA_PRIMARY,
            primaryColorCSS: METANOIA_ACCENT,
            isMetanoia: true,
          } as unknown as ThemeState;
        }
      },
      onRehydrateStorage: () => (state, error) => {
        try {
          if (state && state.theme && state.primaryColor) {
            const isMetanoia = state.primaryColor === METANOIA_PRIMARY;
            state.primaryColorCSS = isMetanoia ? METANOIA_ACCENT : state.primaryColor;
            state.isMetanoia = isMetanoia;
            applyTheme(state.theme, state.primaryColor);
          } else {
            applyTheme('light', '#2563eb');
          }
        } catch (err) {
          console.warn('Theme rehydration failed, using defaults:', err);
          applyTheme('light', '#2563eb');
        }
      },
      partialize: (state) => ({
        theme: state.theme,
        primaryColor: state.primaryColor,
        cachedUserId: state.cachedUserId,
      } as any)
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage) — the look is the ACCOUNT's choice, not the
   device's: it is saved in the account's row, the load already refused another account's cache as a
   signal, and the signed-out landing page paints its own dark ground on purpose (LandingPage). The
   device kept one copy and a sign-out does not reload the page, so the next account opened in the last
   one's look until its own load — and kept it when its row had none; and a late answer for the last
   account dressed the next one's screen in it for the rest of the visit.
   On every change of account the store goes back to the defaults and reads the new account's copy —
   `onRehydrateStorage` applies it (or the defaults) to the page. Signed out, nothing is applied: the
   landing page does not read the theme. */
scopeToAccount(useTheme);
